import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import NotificationBell from './NotificationBell';
import { 
  LayoutDashboard, 
  Lightbulb, 
  FolderKanban, 
  BarChart3, 
  Settings, 
  Map, 
  Video, 
  FileText,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Lock,
  Crown,
  GitBranch,
  History,
  Building2,
  ChevronsUpDown,
  ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import HelpChatbot from './HelpChatbot';
import { cn } from '../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7cce28e4-fe0a-4a9e-8874-d3a0b48d9685/artifacts/e1fqiqg9_logo-square-transparent-13ddc4b%20%281%29.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, enabled: true },
  { path: '/ideas', label: 'Ideas', icon: Lightbulb, enabled: true },
  { path: '/projects', label: 'Projects', icon: FolderKanban, enabled: true },
  { path: '/reporting', label: 'Reporting', icon: BarChart3, enabled: true, minRole: 'Approver' },
  { path: '/executive-summary', label: 'Executive Summary', icon: Crown, enabled: true, minRole: 'Approver' },
  { divider: true },
  { path: '/processes', label: 'Processes', icon: GitBranch, enabled: true },
  { path: '/process-maps', label: 'Process Maps', icon: Map, enabled: true },
  { path: '/process-capture', label: 'Process Capture', icon: Video, enabled: true },
  { path: '/sop-generator', label: 'SOP Generator', icon: FileText, enabled: true },
  { divider: true, minRole: 'Approver' },
  { path: '/audit-log', label: 'Audit Log', icon: History, enabled: true, minRole: 'CI Manager' },
];

const Layout = () => {
  const { user, logout, isAdmin, isMasterAdmin } = useAuth();
  const { activeCompany, companies } = useCompany();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchCompany = () => {
    if (companies.length <= 1 && !isMasterAdmin()) return;
    navigate('/select-company');
  };

  // Filter nav items by role
  const ROLE_RANK = { 'Submitter': 0, 'Approver': 1, 'CI Manager': 2 };
  const userRank = ROLE_RANK[user?.role] ?? 0;
  const visibleNavItems = navItems.filter(item => {
    if (!item.minRole) return true;
    return userRank >= (ROLE_RANK[item.minRole] ?? 0);
  });

  const NavItem = ({ item }) => {
    if (item.divider) {
      return <div className="my-4 border-t border-white/10" />;
    }

    const Icon = item.icon;
    const isDisabled = !item.enabled;

    if (isDisabled) {
      return (
        <div
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/40 cursor-not-allowed",
            sidebarCollapsed && "justify-center px-2"
          )}
          title="Coming Soon"
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {!sidebarCollapsed && (
            <>
              <span className="flex-1 font-medium">{item.label}</span>
              <Lock className="w-4 h-4" />
            </>
          )}
        </div>
      );
    }

    return (
      <NavLink
        to={item.path}
        onClick={() => setMobileMenuOpen(false)}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
            isActive
              ? "bg-[#26B8A7] text-white shadow-lg"
              : "text-white/80 hover:bg-[#1E4B80] hover:text-white",
            sidebarCollapsed && "justify-center px-2"
          )
        }
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#235894] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Beyond Delta" className="w-8 h-8" />
          <span className="font-bold font-['Plus_Jakarta_Sans']">Beyond Delta</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white hover:bg-[#1E4B80]"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-[#235894] text-white shadow-xl transition-all duration-300",
          sidebarCollapsed ? "w-16" : "w-64",
          "hidden lg:block"
        )}
      >
        {/* Logo & Notifications */}
        <div className={cn(
          "flex items-center justify-between p-4 border-b border-white/10",
          sidebarCollapsed && "justify-center"
        )}>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Beyond Delta" className="w-10 h-10" />
            {!sidebarCollapsed && (
              <span className="font-bold text-lg font-['Plus_Jakarta_Sans']">Beyond Delta</span>
            )}
          </div>
          {!sidebarCollapsed && <NotificationBell />}
        </div>

        {/* Active Company Switcher */}
        {!sidebarCollapsed && activeCompany && (
          <button
            onClick={handleSwitchCompany}
            data-testid="company-switcher-btn"
            className="w-full px-4 py-3 border-b border-white/10 hover:bg-[#1E4B80] text-left transition-colors"
            disabled={companies.length <= 1 && !isMasterAdmin()}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-[#26B8A7] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {activeCompany.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/60">Workspace</div>
                <div className="font-medium truncate text-sm">{activeCompany.name}</div>
              </div>
              {(companies.length > 1 || isMasterAdmin()) && <ChevronsUpDown className="w-4 h-4 text-white/60" />}
            </div>
          </button>
        )}

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {visibleNavItems.map((item, index) => (
            <NavItem key={item.path || index} item={item} />
          ))}
          
          {/* Settings - Admin only */}
          {isAdmin() && (
            <>
              <div className="my-4 border-t border-white/10" />
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isActive
                      ? "bg-[#26B8A7] text-white shadow-lg"
                      : "text-white/80 hover:bg-[#1E4B80] hover:text-white",
                    sidebarCollapsed && "justify-center px-2"
                  )
                }
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Settings</span>}
              </NavLink>
            </>
          )}
          {isMasterAdmin() && (
            <NavLink
              to="/master-admin"
              data-testid="nav-master-admin"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                  isActive
                    ? "bg-amber-500 text-white shadow-lg"
                    : "text-white/80 hover:bg-[#1E4B80] hover:text-white",
                  sidebarCollapsed && "justify-center px-2"
                )
              }
            >
              <ShieldCheck className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span className="font-medium">Master Admin</span>}
            </NavLink>
          )}
        </nav>

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={cn(
            "absolute bottom-20 -right-3 w-6 h-6 bg-[#26B8A7] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1fa393] transition-colors",
            "hidden lg:flex"
          )}
          data-testid="sidebar-collapse-btn"
        >
          <ChevronLeft className={cn(
            "w-4 h-4 text-white transition-transform",
            sidebarCollapsed && "rotate-180"
          )} />
        </button>

        {/* User Info & Logout */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 p-3 border-t border-white/10",
          sidebarCollapsed && "p-2"
        )}>
          {!sidebarCollapsed && (
            <div className="mb-3 px-2">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.role}</p>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn(
              "w-full text-white/80 hover:bg-[#1E4B80] hover:text-white",
              sidebarCollapsed ? "px-2" : "justify-start"
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-[#235894] text-white shadow-xl transition-transform duration-300 lg:hidden",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <img src={LOGO_URL} alt="Beyond Delta" className="w-10 h-10" />
          <span className="font-bold text-lg font-['Plus_Jakarta_Sans']">Beyond Delta</span>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {visibleNavItems.map((item, index) => (
            <NavItem key={item.path || index} item={item} />
          ))}
          
          {isAdmin() && (
            <>
              <div className="my-4 border-t border-white/10" />
              <NavLink
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isActive
                      ? "bg-[#26B8A7] text-white shadow-lg"
                      : "text-white/80 hover:bg-[#1E4B80] hover:text-white"
                  )
                }
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Settings</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
          <div className="mb-3 px-2">
            <p className="font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/60 truncate">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full text-white/80 hover:bg-[#1E4B80] hover:text-white justify-start"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="ml-2">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "min-h-screen transition-all duration-300 pt-14 lg:pt-0",
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        )}
      >
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
      <HelpChatbot />
    </div>
  );
};

export default Layout;
